import { Checkbox, Chip, Collapse, Divider, FormControlLabel, TextField } from '@mui/material';
import React from 'react';
import type { ApiKeyApiScopeInput, ApiScopeCatalogItem } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  API_SCOPE_DESCRIPTIONS,
  API_SCOPE_DESCRIPTION_KEYS,
  type ApiKeyUseCaseId,
} from './constants';

interface CreateApiKeyScopePolicyProps {
  selectedUseCase: ApiKeyUseCaseId;
  resourceName: string | undefined;
  presetScopes: ApiKeyApiScopeInput[];
  scopeCatalog: ApiScopeCatalogItem[];
  needsIntegrationClient: boolean;
  showAdvancedScopes: boolean;
  onShowAdvancedChange: (value: boolean) => void;
  advancedScopesJson: string;
  onAdvancedJsonChange: (value: string) => void;
}

export const CreateApiKeyScopePolicy: React.FC<CreateApiKeyScopePolicyProps> = ({
  selectedUseCase,
  resourceName,
  presetScopes,
  scopeCatalog,
  needsIntegrationClient,
  showAdvancedScopes,
  onShowAdvancedChange,
  advancedScopesJson,
  onAdvancedJsonChange,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
            {t('apiKeys.modal.resourceScopes')}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
            {selectedUseCase === 'cliLogin'
              ? t('apiKeys.modal.cliLoginScopeHelper', {
                  defaultValue: 'Specific authorization for AppAI CLI command-line sessions.',
                })
              : resourceName
                ? t('apiKeys.modal.generatedPolicyFor', { name: resourceName })
                : selectedUseCase === 'selectedScopes'
                  ? t('apiKeys.modal.resourceScopesHelper')
                  : t('apiKeys.modal.selectResourceForPolicy')}
          </p>
        </div>
        <Chip
          size="small"
          label={
            selectedUseCase === 'cliLogin'
              ? 'CLI Login'
              : t('apiKeys.modal.scopeCount', { count: presetScopes.length })
          }
          color={selectedUseCase === 'cliLogin' || presetScopes.length > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      </div>

      {selectedUseCase === 'cliLogin' ? (
        <div className="rounded-lg border border-teal-200 bg-teal-50 p-3.5 text-sm text-teal-800 dark:border-teal-800/60 dark:bg-teal-950/30 dark:text-teal-200">
          <p className="mb-1 font-semibold">
            {t('apiKeys.modal.cliLoginPurposeTitle', {
              defaultValue: 'Purpose: CLI Login (Headless / SSH)',
            })}
          </p>
          <p className="text-xs leading-relaxed text-teal-700 dark:text-teal-300/90">
            {t('apiKeys.modal.cliLoginDetails', {
              defaultValue:
                "This key is specifically configured for CLI authentication on headless/SSH servers. When entered into the CLI, the system will verify your account's active package and exchange it for temporary access tokens. No separate API scopes required.",
            })}
          </p>
        </div>
      ) : presetScopes.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          {selectedUseCase === 'selectedScopes'
            ? t('apiKeys.modal.selectScopesToContinue', {
                defaultValue: 'Select at least one API scope to continue.',
              })
            : needsIntegrationClient
              ? t('apiKeys.modal.selectIntegrationClientToContinue')
              : t('apiKeys.modal.selectAgentToContinue')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
          <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)] bg-gray-50 text-xs font-semibold text-gray-500 dark:bg-slate-900 dark:text-slate-400">
            <div className="p-2">{t('apiKeys.modal.scopeColumn')}</div>
            <div className="p-2">{t('apiKeys.modal.resourceColumn')}</div>
            <div className="p-2">{t('apiKeys.modal.meaningColumn')}</div>
          </div>
          {presetScopes.map((scope) => (
            <div
              key={`${scope.scope}:${scope.resourceType ?? ''}:${scope.resourcePublicId ?? ''}`}
              className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)] border-t border-gray-100 text-xs dark:border-slate-700"
            >
              <code className="min-w-0 break-words p-2 text-blue-700 dark:text-blue-300">
                {scope.scope}
              </code>
              <div className="min-w-0 break-words p-2 text-gray-600 dark:text-slate-300">
                {scope.resourceType
                  ? `${scope.resourceType}:${scope.resourcePublicId ?? '*'}`
                  : t('apiKeys.modal.allOwnedResources', {
                      defaultValue: 'All owned resources',
                    })}
              </div>
              <div className="p-2 text-gray-500 dark:text-slate-400">
                {t(API_SCOPE_DESCRIPTION_KEYS[scope.scope] || 'apiKeys.modal.customApiScope', {
                  defaultValue:
                    scopeCatalog.find((item) => item.scope === scope.scope)?.description ||
                    API_SCOPE_DESCRIPTIONS[scope.scope] ||
                    'Custom API scope.',
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedUseCase !== 'cliLogin' && (
        <>
          <Divider className="!my-3" />
          <FormControlLabel
            control={
              <Checkbox
                checked={showAdvancedScopes}
                onChange={(e) => onShowAdvancedChange(e.target.checked)}
                size="small"
              />
            }
            label={
              <span className="text-sm text-gray-700 dark:text-slate-300">
                {t('apiKeys.modal.advancedScopes', {
                  defaultValue: 'Advanced: edit generated scopes JSON',
                })}
              </span>
            }
          />
          <Collapse in={showAdvancedScopes}>
            <TextField
              fullWidth
              value={advancedScopesJson}
              onChange={(e) => onAdvancedJsonChange(e.target.value)}
              size="small"
              multiline
              rows={6}
              placeholder='[{"scope":"agents:execute","resourceType":"agent","resourcePublicId":"agt_..."}]'
              helperText={t('apiKeys.modal.resourceScopesHelper')}
            />
          </Collapse>
        </>
      )}
    </div>
  );
};
